<template>
  <section class="departments-section">
    <div class="container">
      <div class="section-header">
        <h2 v-for="(title, index) in info.home_header2" :key="index">
          {{ title }}
        </h2>
        <p v-for="(subtitle, index) in info.home_header2_subtitle" :key="index">
          {{ subtitle }}
        </p>
      </div>

      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-state">
        <p>加载中...</p>
      </div>

      <!-- 空状态 -->
      <div v-else-if="departments.length === 0" class="empty-state">
        <p>暂无科室信息</p>
      </div>

      <!-- 科室列表 -->
      <div v-else class="departments-grid">
        <div
          class="department-card"
          v-for="dept in departments"
          :key="dept.id"
          @click="handleViewDetail(dept.group)"
        >
          <div>
            <img class="service-icon" :src="dept.image" :alt="dept.name" />
            <h3>{{ dept.name }}</h3>
          </div>
          <p class="text-indent">{{ dept.descmain }}</p>
          <div class="btn-small">查看详情</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { type DepartmentType } from "@/stores/departments";

const router = useRouter();
const isLoading = computed(() => prop.departments.length === 0);

interface titleInfoType {
  home_header2?: string[];
  home_header2_subtitle?: string[];
}

interface SectionProps {
  info: titleInfoType;
  departments: DepartmentType[];
}
const prop = withDefaults(defineProps<SectionProps>(), {});

// 处理查看详情点击
const handleViewDetail = (group: string) => {
  router.push({ name: "Departments", params: { group } });
};
</script>

<style scoped>
/* 内容区域通用样式 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 50px;
}

.section-header h2 {
  font-size: 2.5rem;
  color: #1a5fb4;
  margin-bottom: 15px;
}

.section-header p {
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
}

/* 加载和空状态 */
.loading-state,
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #999;
  font-size: 1.1rem;
}

/* 科室展示 */
.departments-section {
  padding: 80px 0;
  background-color: white;
}

.departments-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.department-card {
  background: #f8f9fa;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-height: 280px;
}

.department-card:hover {
  background: #1a5fb4;
  color: white;
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.department-card:hover h3,
.department-card:hover p {
  color: white;
}

.department-card:hover .btn-small {
  background: white;
  color: #1a5fb4;
}

.service-icon {
  width: 50px;
  height: 50px;
  object-fit: contain;
  margin: 0 auto;
  margin-bottom: 20px;
}

.department-card h3 {
  margin-bottom: 15px;
  color: #333;
  font-size: 1.2rem;
}

.department-card p {
  margin-bottom: 20px;
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
}

.btn-small {
  padding: 8px 20px;
  font-size: 0.9rem;
  display: inline-block;
  text-decoration: none;
  text-align: center;
  background-color: #1a5fb4;
  color: white;
  border-radius: 5px;
  transition: all 0.3s;
}

.btn-small:hover {
  background-color: #0d7df7;
}

/* 平板端适配 - 2列布局 */
@media (max-width: 1024px) {
  .departments-section {
    padding: 60px 0;
  }

  .departments-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .department-card {
    padding: 25px;
    min-height: 260px;
  }
}

/* 移动端适配 - 单列布局 */
@media (max-width: 768px) {
  .departments-section {
    padding: 50px 0;
  }

  .container {
    padding: 0 15px;
  }

  .section-header {
    margin-bottom: 40px;
  }

  .section-header h2 {
    font-size: 2rem;
  }

  .section-header p {
    font-size: 1rem;
  }

  .departments-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .department-card {
    padding: 20px;
    min-height: auto;
  }

  .service-icon {
    width: 45px;
    height: 45px;
    margin: 0 auto;
    margin-bottom: 15px;
  }

  .department-card h3 {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }

  .department-card p {
    font-size: 0.9rem;
    margin-bottom: 15px;
  }

  .btn-small {
    width: 100%;
    padding: 10px 20px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .departments-section {
    padding: 40px 0;
  }

  .container {
    padding: 0 10px;
  }

  .section-header h2 {
    font-size: 1.6rem;
  }

  .department-card {
    padding: 18px;
  }

  .service-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto;
    margin: 10px auto;
  }
}
</style>
